import { landingApiRequest } from '@/lib/landing-api'
import {
  fetchLandingPixPaymentStatus,
  type LandingPixPaymentStatusData,
} from '@/lib/landing-payments'

export const OBRIGADO_ACCOUNT_REDIRECT_MS = 3000

const CHECKOUT_PROOF_STORAGE_PREFIX = 'legalprime:paymentAutoAuthCheckout:'

export type PaymentAutoAuthCheckoutProof = {
  checkoutToken: string
  savedAt: number
}

export function savePaymentAutoAuthCheckoutProof(
  paymentId: number,
  checkoutToken: string
): void {
  if (typeof window === 'undefined' || paymentId <= 0 || checkoutToken.trim() === '') {
    return
  }

  const payload: PaymentAutoAuthCheckoutProof = {
    checkoutToken: checkoutToken.trim(),
    savedAt: Date.now(),
  }
  try {
    window.sessionStorage.setItem(
      `${CHECKOUT_PROOF_STORAGE_PREFIX}${paymentId}`,
      JSON.stringify(payload)
    )
  } catch {
    // Fluxo segue; a troca pode falhar e o usuário faz login manual.
  }
}

export function readPaymentAutoAuthCheckoutProof(
  paymentId: number
): PaymentAutoAuthCheckoutProof | null {
  if (typeof window === 'undefined' || paymentId <= 0) {
    return null
  }

  try {
    const raw = window.sessionStorage.getItem(
      `${CHECKOUT_PROOF_STORAGE_PREFIX}${paymentId}`
    )
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as PaymentAutoAuthCheckoutProof
    if (
      typeof parsed.checkoutToken !== 'string' ||
      parsed.checkoutToken.trim() === '' ||
      !Number.isFinite(parsed.savedAt) ||
      Date.now() - parsed.savedAt > 5 * 60 * 1000
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearPaymentAutoAuthCheckoutProof(paymentId: number): void {
  if (typeof window === 'undefined' || paymentId <= 0) {
    return
  }
  try {
    window.sessionStorage.removeItem(`${CHECKOUT_PROOF_STORAGE_PREFIX}${paymentId}`)
  } catch {
    // noop
  }
}

export type LandingPaymentAutoAuthExchangeInput = {
  institution_id: number
  payment_id: number
  auto_auth_token: string
  checkout_token?: string
}

export async function landingExchangePaymentAutoAuth(
  input: LandingPaymentAutoAuthExchangeInput
): Promise<void> {
  await landingApiRequest('/api/donor/campaign-account/payment-auto-auth/exchange', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

const AUTO_AUTH_STATUS_RETRY_DELAYS_MS = [0, 400, 900, 1500] as const

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

export async function fetchPaymentAutoAuthTokenWithRetry(
  campaignId: number,
  paymentId: number,
  checkoutProofToken: string
): Promise<string | null> {
  let lastStatus: LandingPixPaymentStatusData | null = null

  for (const delayMs of AUTO_AUTH_STATUS_RETRY_DELAYS_MS) {
    if (delayMs > 0) {
      await waitMs(delayMs)
    }

    const status = await fetchLandingPixPaymentStatus(null, campaignId, paymentId, {
      checkoutToken: checkoutProofToken,
    })
    lastStatus = status

    if (status.status === 'paid') {
      const token = (
        status as LandingPixPaymentStatusData & { payment_auto_auth_token?: string }
      ).payment_auto_auth_token
      if (typeof token === 'string' && token.trim() !== '') {
        return token.trim()
      }
    }
  }

  if (lastStatus?.status === 'paid') {
    return null
  }

  return null
}

export function buildAccountAutoAuthSearchParams(input: {
  paymentId: number
  autoAuthToken?: string | null
}): URLSearchParams {
  const params = new URLSearchParams()
  params.set('return', 'account')
  params.set('payment_id', String(input.paymentId))
  if (input.autoAuthToken) {
    params.set('auto_auth_token', input.autoAuthToken)
  }
  return params
}

export function stripPaymentAutoAuthParamsFromUrl(): void {
  if (typeof window === 'undefined') {
    return
  }

  const url = new URL(window.location.href)
  const keys = ['auto_auth_token', 'payment_id']
  let changed = false
  for (const key of keys) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key)
      changed = true
    }
  }
  if (!changed) {
    return
  }

  const query = url.searchParams.toString()
  const next = `${url.pathname}${query ? `?${query}` : ''}${url.hash}`
  window.history.replaceState(window.history.state, '', next)
}
